import React, {
	useCallback,
	useMemo,
	useEffect,
	memo,
	type KeyboardEvent,
	type ChangeEvent,
	useImperativeHandle,
} from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { CellWrapper } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { CellNavigationProps } from '../../../common/types/cell-navigation.tsx';
import { SmartLink } from './link/index.tsx';
import messages from './messages.tsx';

export type TextCellProps = {
	issueKey: IssueKey;
	isEditable: boolean;
	fieldKey: string;
	fieldType: string;
	fieldValue: string | null;
	fieldName?: string;
	issueType?: string;
	issueTypeId?: string;
} & CellNavigationProps;

export const UrlCell = memo<TextCellProps>(
	({
		issueKey,
		fieldKey,
		isEditable,
		fieldValue,
		fieldType,
		fieldName = '',
		issueType = '',
		issueTypeId = '',
		editMode = false,
		setEditMode = noop,
		cellRef,
		inputValue: textValue = fieldValue ?? '',
		onInputChange: setTextValue = noop,
	}: TextCellProps) => {
		const [{ value, error }, { saveValue, resetError }] = useEditField<string | null, null>({
			fieldKey,
			issueKey,
			fieldType,
			initialValue: fieldValue,
		});
		const { createAnalyticsEvent } = useAnalyticsEvents();

		useEffect(() => {
			// reset textValue when there's an error
			if (error && value !== textValue) {
				setTextValue(value ?? '');
			}
		}, [error, setTextValue, textValue, value]);

		const saveAndClose = useCallback(() => {
			if (editMode) {
				setEditMode(false);

				if (textValue !== value) {
					saveValue(textValue, null, createAnalyticsEvent({}));
				}
			}
		}, [createAnalyticsEvent, editMode, saveValue, setEditMode, textValue, value]);

		const handleChange = useCallback(
			(event: ChangeEvent<HTMLInputElement>) => {
				setTextValue(event.target.value);
				resetError();
			},
			[resetError, setTextValue],
		);

		const handleKeyDown = useCallback(
			(event: KeyboardEvent<HTMLElement>) => {
				if (editMode && event.code === 'Escape') {
					setTextValue(value ?? '');
				}
				return false;
			},
			[editMode, setTextValue, value],
		);

		const cellOverrides = useMemo(
			() => () => ({
				onKeyDown: handleKeyDown,
			}),
			[handleKeyDown],
		);
		useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

		const handleSubmit = useCallback(() => {
			saveAndClose();
		}, [saveAndClose]);

		const cellContent = (
			<>
				{editMode ? (
					<form onSubmit={handleSubmit}>
						<Textfield autoFocus onBlur={saveAndClose} value={textValue} onChange={handleChange} />
					</form>
				) : (
					value != null && <SmartLink value={value} />
				)}
				{error && (
					<FixItErrorFlag
						error={error}
						title={messages.errorTitle}
						description={messages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={fieldKey}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		return (
			<CellWrapper
				isEditable={isEditable}
				testId="business-list.ui.list-view.url-cell.url-cell-wrapper"
				hasPadding={false}
			>
				<Wrapper>{cellContent}</Wrapper>
			</CellWrapper>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NonSmartLinkWrapper = styled.div({
	display: 'flex',
	flex: 1,
	cursor: 'text',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	form: {
		display: 'flex',
		alignItems: 'center',
		height: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> input': {
			height: '100%',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> div': {
			height: '100%',
		},
	},
	whiteSpace: 'nowrap',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	a: {
		display: 'flex',
		justifyContent: 'flex-start',
		alignItems: 'center',
		maxWidth: '100%',
		marginTop: token('space.100'),
		marginRight: token('space.100'),
		marginBottom: token('space.100'),
		marginLeft: token('space.100'),
		position: 'relative',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'[data-component-selector="business-list.ui.list-view.url-cell.link-follower"]': {
				display: 'flex',
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&[data-smart-element-link]': {
			margin: 0,
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SmartLinkWrapper = styled.div({
	display: 'flex',
	flex: 1,
	cursor: 'text',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	form: {
		display: 'flex',
		alignItems: 'center',
		height: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> input': {
			height: '100%',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> div': {
			height: '100%',
		},
	},
	whiteSpace: 'nowrap',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > a': {
		display: 'flex',
		justifyContent: 'flex-start',
		alignItems: 'center',
		maxWidth: '100%',
		marginTop: token('space.100'),
		marginRight: token('space.100'),
		marginBottom: token('space.100'),
		marginLeft: token('space.100'),
		position: 'relative',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'[data-component-selector="business-list.ui.list-view.url-cell.link-follower"]': {
				display: 'flex',
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&[data-smart-element-link]': {
			margin: 0,
		},
	},
});

const Wrapper = componentWithCondition(
	() => expValEquals('jsw_perf_format-links-in-custom-fields', 'list-view', true),
	SmartLinkWrapper,
	NonSmartLinkWrapper,
);
